import { toast } from 'react-toastify';

// Error severity levels
export const ERROR_SEVERITY = {
  HIGH: 'high',     // For 5xx errors
  MEDIUM: 'medium', // For 4xx errors
  LOW: 'low'       // For handled business logic errors
};

// Get severity level based on status code
export const getErrorSeverity = (status) => {
  if (status >= 500) return ERROR_SEVERITY.HIGH;
  if (status >= 400) return ERROR_SEVERITY.MEDIUM;
  return ERROR_SEVERITY.LOW;
};

/**
 * Utility function to get formatted error messages from API responses
 * @param {Error} error - The error object from API response
 * @returns {Object} Object containing status, message and severity
 */
export const getErrorMessage = (error) => {
  const status = error?.response?.status;
  const responseData = error?.response?.data;
  const backendError = responseData?.error;
  const backendMessage = responseData?.message;
  const errorCode = responseData?.code;

  // If we have a backend error message, prioritize it
  if (backendError || backendMessage) {
    return {
      status,
      message: backendMessage || backendError,
      severity: getErrorSeverity(status),
      code: errorCode
    };
  }

  // HTTP status code based messages
  const statusMessages = {
    400: 'Invalid request parameters',
    401: 'Authentication required',
    403: 'You do not have permission to perform this action',
    404: 'Resource not found',
    409: 'Resource conflict',
    422: 'Invalid data provided',
    429: 'Too many requests',
    500: 'Internal server error',
    502: 'Service temporarily unavailable',
    503: 'Service unavailable',
    504: 'Request timeout'
  };

  const message = status
    ? statusMessages[status] || `Server returned status ${status}`
    : error?.message || 'An unexpected error occurred';

  return {
    status,
    message,
    severity: getErrorSeverity(status),
    code: errorCode
  };
};

/**
 * Utility function to handle API errors with toast notifications
 * @param {Error} error - The error object from API response
 * @param {string} context - Context message for the error (e.g., "Failed to Delete")
 * @param {Object} params - Optional parameters to include in the error message
 * @returns {Object} Object containing the formatted error message and severity
 */
export const handleApiError = (error, context, params = {}) => {
  // If the error is marked silent by our apiClient interceptor, do not toast
  if (error && error.silent) {
    return { message: context || 'No access', severity: ERROR_SEVERITY.LOW, status: 400 };
  }

  const { status, message, severity } = getErrorMessage(error);

  // Build error components
  const errorPrefix = 'ERROR';
  const statusCode = status ? ` ${status}` : '';
  const contextMsg = context ? ` - ${context}` : '';
  const paramsMsg = Object.keys(params).length > 0
    ? ` (${Object.entries(params).map(([k, v]) => `${k}: ${v}`).join(', ')})`
    : '';
  const specificMsg = message && !message.toLowerCase().includes(context.toLowerCase())
    ? ` - ${message}`
    : '';

  // Combine components into final message
  const errorMessage = `${errorPrefix}${statusCode}${contextMsg}${paramsMsg}${specificMsg}`;

  // Configure toast based on severity
  const toastConfig = {
    [ERROR_SEVERITY.HIGH]: {
      autoClose: false,      // Requires manual dismissal
      className: 'error-toast-high'
    },
    [ERROR_SEVERITY.MEDIUM]: {
      autoClose: 5000,       // 5 seconds
      className: 'error-toast-medium'
    },
    [ERROR_SEVERITY.LOW]: {
      autoClose: 3000,       // 3 seconds
      className: 'error-toast-low'
    }
  };

  // Log full error details for debugging
  console.error({
    message: errorMessage,
    status,
    context,
    params,
    severity,
    originalError: error
  });

  toast.error(errorMessage, toastConfig[severity]);

  return {
    message: errorMessage,
    severity,
    status
  };
}; 